import { Component, OnInit, OnDestroy } from '@angular/core';
import { AuthService } from './shared/services/auth/auth.service';
import { Keepalive } from '@ng-idle/keepalive';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { InsightsService } from './shared/services/Insights/insights.service';
import { ToastrService } from 'ngx-toastr';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { filter, takeUntil, map } from 'rxjs/operators';
import { Subject, Subscription, BehaviorSubject, interval } from 'rxjs';
import { HttpService } from './shared/services/http/http.service';
import { MatDialog } from "@angular/material/dialog";
import { SessionWarningDialogComponent, SessionWarningDialogModel } from "src/app/modules/package-management/dialogue/session-warning-dialog/session-warning-dialog.component";
import { ConfigurationService } from "src/app/shared/services/configuration/configuration.service";
import { apiConstants } from './shared/constants/endpoints';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})

export class AppComponent implements OnInit, OnDestroy {

  private idleSubscriptions: Subscription[] = [];
  private isWarningDialogOpen = false;
  private countdown$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  appSetting: any;
  private countdownSub: Subscription;

  constructor(
    private insights: InsightsService,
    private toastr: ToastrService,
    private idle: Idle,
    private keepalive: Keepalive,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private authService: AuthService,
    private http: HttpService,
    private configService: ConfigurationService,
    private dialog: MatDialog
  ) {

  }

  title = 'AGN.CoolSculpting.WebApp';
  isExpanded = false;
  menuExpanded = false;
  menuClass = false;
  isAuthenticated = false;
  private readonly _destroying$ = new Subject<void>();

  ngOnInit() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: any) => {
        this.msalService.instance.setActiveAccount(result.payload.account);
      });

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None)
      , takeUntil(this._destroying$))
      .subscribe(() => {
        const account = this.msalService.instance.getActiveAccount();
        if (!account) {
          if (window.location.pathname !== '/' && !localStorage.getItem('routeURL')) {
            localStorage.setItem('routeURL', window.location.pathname + window.location.search);
          }
          this.msalService.loginRedirect();
          return;
        } 
        (async () => {
          try {
            await this.configService.loadSecureConfig();
            const shouldRedirect = window.location.pathname === '/' && !localStorage.getItem('routeURL');
            this.authService.checkAuth(account.name, account.username, account.idToken, shouldRedirect);
            this.userSessionAction();
          } catch (err: any) {
            if (err?.name === 'InteractionRequiredAuthError' || err?.errorMessage?.includes('interaction_required')) {
              this.msalService.loginRedirect();
            } else {
              console.error('Unexpected error during startup:', err);
            }
          }
        })();        
      });
    this.authService.userName?.pipe(takeUntil(this._destroying$)).subscribe((data: any) => {
      this.isAuthenticated = data ? true : false;
    });
    
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
    // Unsubscribe from all idle subscriptions
    if (this.idleSubscriptions) {
      this.idleSubscriptions.forEach((sub) => sub.unsubscribe());
    }
  }

  toggle() {
    this.isExpanded = !this.isExpanded;
  }

  toggleMenu() {
    this.menuExpanded = !this.menuExpanded;
    this.menuClass = !this.menuClass;
  }

  userSessionAction() {
    // sets an idle timeout of 30 minutes
    this.appSetting = this.configService.getAppSettings();
    const timeoutMinutes = this.appSetting.sessionTimeoutMinutes;
    if (timeoutMinutes) {
      this.idle.setIdle(timeoutMinutes * 60);
    } else {
      this.idle.setIdle(1800);
    }

    // sets a timeout period of 10 seconds. after 30 minutes of inactivity, the user will be considered timed out.
    this.idle.setTimeout(10);

    // sets the interrupts to listen for
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // starts the idle timer and pings every 15 seconds
    this.idle.watch();

    // Set up keepalive ping to keep the user session alive while idle -  5 min
    this.keepalive.interval(300);
    this.keepalive.start();    

    this.idleSubscriptions.push(this.idle.onIdleStart.subscribe(() => {
      this.idle.setInterrupts([]);
      if (!this.isWarningDialogOpen) {
        this.startCustomCountdown(10);
      }
    })
    );


    // resets the idle timer when the user becomes active again
    document.addEventListener('mousemove', () => {
      this.idle.watch();
    });

    // // logs out the user when the total timeout period has elapsed
    this.idle.onTimeout.subscribe(() => {
      this.insights.trackEvent(
        'CD Support-Auth App Component - User is not active on application'
      );
      this.toastr.warning(
        'You have been logged out due to inactivity',
        'Logout'
      );
      this.http.get(`${apiConstants.tokenBlacklist}?isRequestedFromLogout=false`).subscribe((data: any) => {
        localStorage.clear();
        sessionStorage.clear();
        this.msalService.logoutRedirect();
      });
    });
  }

  private startCustomCountdown(seconds: number): void {
    let counter = seconds;
    this.countdown$.next(counter);

    this.countdownSub = interval(1000).subscribe(() => {
      counter--;
      this.countdown$.next(counter);

      if (counter <= 0) {
        this.countdownSub.unsubscribe();
        this.http.get(`${apiConstants.tokenBlacklist}?isRequestedFromLogout=false`).subscribe((data: any) => {
          localStorage.clear();
          sessionStorage.clear();
          this.msalService.logoutRedirect();
        });
      }
    });

    this.showWarningDialog(counter); // open your dialog
  }

  private showWarningDialog(countdown: number): void {
    this.isWarningDialogOpen = true;
    const countdownMessage$ = this.countdown$.pipe(
      map((count: number) => `Your session will expire in ${count} seconds. Do you want to continue?`)
    );
    const dialogData = new SessionWarningDialogModel("Session Expiration Warning", countdownMessage$);
    const dialogRef = this.dialog.open(SessionWarningDialogComponent, {
      width: '300px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      this.isWarningDialogOpen = false;
      this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
      if (result === 'continue') {
        this.countdownSub?.unsubscribe();         
        this.idle.watch(); // Reset the timer if the user continues
      } else {
        // Logout if the user doesn't respond
        this.http.get(`${apiConstants.tokenBlacklist}?isRequestedFromLogout=true`).subscribe((data: any) => {
          localStorage.clear();
          sessionStorage.clear();
          this.msalService.logoutRedirect();
        });        
      }
    });
  }
}

